/* =============================================================
   Aëlvir — contenu FR de la page unique.
   Positionnement : Aëlvir est un STUDIO de création. Cywiz est le
   produit qu'il a construit, et la porte d'entrée commerciale.

   Règles éditoriales à ne pas casser :
   • « organisme de formation » est INTERDIT hors mentions légales.
     Le statut existe juridiquement, mais l'afficher promet un
     catalogue qui n'existe pas.
   • Le présentiel et la location de casques sont des MODALITÉS DE
     CYWIZ, pas des expertises Aëlvir. Le détail vit sur cywiz.fr.
   • Le produit s'écrit « Cywiz » avec VR en exposant (<CywizMark />).
   • Rien d'inventé : pas de logo ni de référence client fictifs.
     Les preuves affichées sont Cywiz, sa validation cybeRéponse,
     et les indicateurs mesurés du produit.

   Ce fichier est la source du copy FR (i18n.jsx ne fait que brancher le provider).
   ============================================================= */

export const fr = {
  nav: {
    studio: "Le studio",
    methode: "Méthode",
    contact: "Contact",
  },
  contactCta: "Parler d'un projet",

  common: {
    backToTop: "Remonter en haut",
    skipToContent: "Aller au contenu",
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    switchLang: "Changer de langue",
    home: "Aëlvir, accueil",
    close: "Fermer",
    newWindow: "(nouvelle fenêtre)",
    logoAlt: "AËLVIR · Aborder, Apprendre, Avancer",
    cywizProductAlt: "Cywiz : tableau de bord et casque de réalité virtuelle",
  },

  seo: {
    title: "Aëlvir · Studio de serious games et de formations en réalité virtuelle",
    description:
      "Aëlvir crée des dispositifs de formation, serious games 2D/3D et réalité virtuelle. Éditeur de Cywiz, application de sensibilisation à la cybersécurité déjà déployée.",
  },

  org: {
    name: "Aëlvir",
    street: "7 Rue Auguste Rodin",
    city: "28630 Le Coudray",
    country: "France",
    email: "contact.aelvir@proton.me",
    phone: "06 60 98 48 16",
    url: "https://aelvir.fr",
  },

  /* ---------------- 1. Hero ---------------- */
  hero: {
    motto: ["Aborder", "Apprendre", "Avancer"],
    titleLines: ["Nous ouvrons", "les portes", "des compétences"],
    subtitle:
      "Aëlvir conçoit des expériences interactives sur mesure, du serious game 2D/3D à la réalité virtuelle.",
    primaryBtn: "Découvrir {cywiz}",
    secondaryBtn: "Parler d'un projet",
    scroll: "Défiler",
  },

  /* ---------------- 2. Le problème ---------------- */
  problem: {
    kicker: "Le constat",
    title: ["Le message passe,", "Le geste, lui, ne change pas"],
    desc:
      "Trois situations reviennent dans presque tous les projets qui arrivent chez nous. Trois freins qui limitent l'impact. Et que nous aidons à dépasser.",
    columns: ["Le problème", "La conséquence", "Notre réponse"],
    rows: [
      {
        title: "Un message qui ne laisse pas de trace",
        problem:
          "Informer, former, sensibiliser : le contenu est suivi, consulté, mais rien n'en reste vraiment.",
        consequenceTitle: "L'information ne change rien",
        consequence:
          "Elle est reçue passivement. Les comportements et les prises de conscience ne durent pas.",
        responseTitle: "Nous créons des expériences qui marquent",
        response:
          "Mécaniques engageantes, mises en situation, feedback immédiat : nos contenus sont vécus, compris et retenus.",
      },
      {
        title: "Une situation coûteuse ou difficile à reproduire",
        problem:
          "Le réel est cher, rare, risqué ou tout simplement inaccessible pour s'entraîner ou expérimenter.",
        consequenceTitle: "Peu d'occasions de répéter ou de comprendre",
        consequence:
          "Les mises en situation sont rares, incomplètes. Les difficultés persistent.",
        responseTitle: "Nous simulons le réel, sans risque ni contrainte",
        response:
          "Environnements fidèles, scénarios ajustables : chacun peut s'exercer, découvrir ou progresser en sécurité.",
      },
      {
        title: "Un public qu'on n'atteint plus",
        problem:
          "Profils, âges, contextes très variés. Les formats classiques ne parlent pas à tout le monde.",
        consequenceTitle: "Faible engagement, impact invisible",
        consequence:
          "L'attention décroche vite, et les effets réels restent difficiles à mesurer.",
        responseTitle: "Nous concevons des expériences qui donnent envie d'agir",
        response:
          "Formats immersifs et ludiques, adaptables à chaque public : chacun reste engagé, chacun progresse.",
      },
    ],
    conviction: {
      label: "Notre conviction",
      title: ["Le bon message ne suffit pas.", "Il faut la bonne expérience."],
      text:
        "Nous concevons des expériences interactives sur mesure qui transforment une intention en expérience concrète, engageante et mémorable.",
      cta: "Découvrir notre approche",
    },
  },

  /* ---------------- 3. Le studio ---------------- */
  studio: {
    kicker: "Le studio",
    title: ["Ce que nous", "créons"],
    desc:
      "Un même projet, décliné selon le support qui sert l'objectif : casque, écran, tablette ou navigateur, immersif ou non. Pas de catalogue : nous partons toujours de votre besoin de sensibiliser, former ou faire prendre conscience.",
    idealLabel: "Idéal pour",
    items: [
      {
        verb: "Concevoir",
        title: "Serious games",
        hook: "Faire comprendre, sensibiliser, faire agir.",
        desc: "Des jeux où chaque mécanique répond à un objectif pédagogique explicite. Si un élément amuse sans rien apprendre, il saute.",
        ideal: [
          "Sensibilisation · apprentissage",
          "Communication · comportement",
          "Observance · adhésion",
          "Mise en situation · décision",
        ],
      },
      {
        verb: "Immerger",
        title: "Réalité virtuelle & simulation",
        hook: "S'entraîner sans les contraintes du réel.",
        desc: "Le geste à l'échelle réelle, dans un environnement reconstruit. Répétable autant de fois que nécessaire, sans risque ni immobilisation.",
        ideal: [
          "Gestes · procédures",
          "Situations à risque",
          "Prise de décision",
          "Ancrage mémoriel",
        ],
      },
      {
        verb: "Construire",
        title: "Sur mesure",
        hook: "Quand votre besoin ne rentre dans aucune case.",
        desc: "Des expériences pensées pour vos enjeux spécifiques. De l'idée au déploiement, nous construisons avec vous la solution la plus pertinente.",
        ideal: [
          "Expérience spécifique",
          "Simulation métier",
          "Dispositif hybride",
          "Projet innovant",
        ],
      },
    ],
  },

  /* ---------------- 4. Cywiz — la preuve ---------------- */
  cywiz: {
    kicker: "Déjà prêt",
    tagline: "Notre application cybersécurité, déployée sur le terrain",
    lead:
      "{cywiz} : la cybersécurité s'apprend en la vivant. Déployée en entreprise et en centre de formation, notre solution de réalité virtuelle a déjà fait ses preuves.",
    validation:
      "Contenus validés par cybeRéponse, le centre régional de réponse aux incidents cyber (Centre-Val de Loire).",
    proofs: [
      {
        title: "Conçu par Aëlvir",
        text: "Une solution née de notre méthode studio, de l'idée au déploiement.",
      },
      {
        title: "Testé en conditions réelles",
        text: "Éprouvé avec de vrais apprenants, en entreprise et en formation.",
      },
      {
        title: "Déployé sur le terrain",
        text: "Déjà en service auprès d'organisations qui forment au quotidien.",
      },
    ],
    bridge: {
      kicker: "Et pour vous ?",
      title: [
        "Ce savoir-faire ne s'arrête pas à {cywiz}",
        "Appliquons-le à votre projet.",
      ],
      text:
        "Quel que soit votre besoin, nous mettons notre\nexpertise à votre service pour concevoir\nla solution la plus adaptée à vos objectifs.",
      cta: "Parlons de votre projet",
    },
  },

  /* ---------------- 5. Méthode courte ---------------- */
  method: {
    kicker: "Méthode",
    title: ["Valider l'idée", "avant de produire"],
    desc:
      "Un projet ne dérive pas par manque de talent : il dérive lorsqu'on produit avant d'avoir vérifié que le concept fonctionne.",
    steps: [
      { title: "Cadrage", desc: "Objectifs, publics, contraintes réelles. Les indicateurs de réussite sont posés avant la conception." },
      { title: "Prototype jouable", desc: "Une version brute, testée par de vrais utilisateurs. On corrige au fil de l'eau, tant que ça coûte peu." },
      { title: "Production", desc: "2D / 3D, audio, IA le cas échéant. Des builds intermédiaires testables, pas d'effet tunnel." },
      { title: "Déploiement", desc: "Mise en service, formation des équipes, puis lecture des traces pour ajuster." },
    ],
  },

  /* ---------------- 6. FAQ ---------------- */
  faq: {
    kicker: "Questions directes",
    title: ["Ce qu'on nous", "demande vraiment"],
    items: [
      {
        q: "Vous avez un catalogue de formations ?",
        a: "Non, et c'est volontaire. Aëlvir est un studio : nous concevons et développons des dispositifs. Le seul produit prêt à l'emploi que nous éditons, {cywiz}. Pour tout le reste, on part de vos procédures, de votre réalité terrain et des erreurs qui se répètent chez vous, pas d'une étagère.",
      },
      {
        q: "{cywiz} ou du sur-mesure : comment je choisis ?",
        a: "Si votre sujet est la sensibilisation à la cybersécurité, {cywiz} existe, il est testé, validé et déployable tout de suite : inutile de reconstruire. Si votre sujet est un geste métier, une procédure interne ou un contexte propre à votre organisation, il faut le construire : c'est exactement notre travail.",
      },
      {
        q: "Combien de temps avant d'avoir quelque chose de jouable ?",
        a: "Un prototype jouable arrive avant la production lourde. Il n'a ni les assets définitifs ni l'habillage final : il sert à répondre à une seule question, est-ce que cet exercice apprend quelque chose. S'il ne convainc pas, on corrige à ce moment-là, pas trois mois plus tard.",
      },
      {
        q: "Quel budget prévoir pour un projet sur mesure ?",
        a: "Chaque projet a son enveloppe. On pose le cadrage avant de chiffrer : objectifs, supports, volume de contenu. Un ordre de grandeur est partagé tôt, sans engagement figé au premier échange.",
      },
      {
        q: "Quel matériel est nécessaire pour utiliser vos solutions ?",
        a: "Selon le support : casque VR, PC, tablette ou navigateur. Pour la VR, les casques compatibles sont précisés au cadrage. Le détail matériel de {cywiz} est sur cywiz.fr.",
      },
      {
        q: "Qui fournit les contenus et les scénarios ?",
        a: "Les contenus partent de vos procédures, cas réels et experts métier. Nous les concevons et les produisons avec vous : vous apportez la matière, nous la mettons en expérience.",
      },
    ],
  },

  /* ---------------- 7. Besoin spécifique (pont FAQ → contact) ---------------- */
  need: {
    kicker: "Vous avez un besoin spécifique ?",
    title: [
      "Chaque situation mérite une solution adaptée.",
      "Parlons de votre projet.",
    ],
    text:
      "Vous avez un besoin de sensibilisation, une situation difficile à reproduire ou un geste à transmettre ? Échangeons sur vos enjeux et explorons ensemble la solution la plus adaptée à vos objectifs.",
    pillars: [
      {
        title: "Un besoin spécifique ?",
        text: "Des objectifs métiers à atteindre.",
      },
      {
        title: "Une situation complexe à reproduire ?",
        text: "Nous la transformons en expérience efficace.",
      },
      {
        title: "Un geste à transmettre ?",
        text: "Nous le rendons immersif, clair et mémorable.",
      },
    ],
  },

  /* ---------------- 8. Contact ---------------- */
  contact: {
    kicker: "Contactez-nous",
    title: ["Parlons de", "votre projet"],
    desc: "Décrivez-nous votre besoin, nous vous recontacterons rapidement pour en discuter.",
    labels: {
      name: "Nom et prénom",
      company: "Organisation",
      email: "Email professionnel",
      type: "Type de demande",
      message: "Message",
    },
    placeholders: {
      name: "Nom et prénom",
      company: "Entreprise, association, OF, collectivité…",
      email: "Email professionnel",
      message: "Décrivez votre projet ou votre besoin…",
    },
    typePlaceholder: "Votre besoin concerne…",
    requestTypes: [
      { value: "cywiz", label: "Cywiz : déploiement, licences, session" },
      { value: "sur-mesure", label: "Un dispositif sur mesure" },
      { value: "autre", label: "Autre demande" },
    ],
    promises: [
      {
        title: "Réponse rapide",
        text: "Nous vous recontactons sous 48h.",
      },
      {
        title: "Confidentialité garantie",
        text: "Vos informations restent strictement confidentielles.",
      },
      {
        title: "Sans engagement",
        text: "Un premier échange pour vous conseiller.",
      },
    ],
    secure: "Vos données sont sécurisées",
    rgpd: {
      required: "* Tous les champs sont obligatoires.",
      summary: "Information RGPD sur le traitement de votre demande",
      paragraphs: [
        "Ce formulaire permet de contacter Aëlvir pour toute question générale, demande d'information ou projet. La base légale du traitement est l'article 6.1.a du RGPD : votre consentement, exprimé par l'envoi volontaire du formulaire.",
        "L'ensemble des champs du formulaire est obligatoire : ils sont nécessaires au traitement de votre demande, que vous ne pourrez pas envoyer sans eux.",
        "Les données transmises sont destinées à Aëlvir, à ses personnes internes chargées de traiter les demandes, à Infomaniak Network SA pour l'hébergement du site et l'envoi SMTP du formulaire, puis à Proton AG pour la conservation des messages reçus dans la boîte e-mail de contact. Elles peuvent aussi être communiquées à toute personne légalement autorisée.",
        "Les données sont conservées le temps nécessaire au traitement de votre demande, puis au maximum trois ans à compter du dernier échange. Vous pouvez demander leur suppression à tout moment.",
        "Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité, ainsi que du droit de retirer votre consentement à tout moment. Pour exercer vos droits : contact.aelvir@proton.me. Vous pouvez également introduire une réclamation auprès de la CNIL.",
        "Le responsable du traitement est Aëlvir, société par actions simplifiée à capital variable, 7 Rue Auguste Rodin, 28630 Le Coudray, France.",
      ],
    },
    submit: "Envoyer ma demande",
    sending: "Envoi…",
    toast: {
      required: "Merci de remplir tous les champs obligatoires.",
      fieldRequired: "Ce champ est obligatoire.",
      success: "Message envoyé ! Nous vous répondrons rapidement.",
      mocked: "Formulaire OK, mais l'email n'est pas configuré (SMTP / .env).",
      error: "Une erreur est survenue. Réessayez.",
      fail: "Échec de l'envoi. Vérifiez vos informations et réessayez.",
    },
  },

  footer: {
    nav: "Navigation",
    product: "Le produit",
    contact: "Coordonnées",
    rights: "Tous droits réservés.",
  },

  legal: {
    trigger: "Mentions légales",
    title: "Mentions légales",
    sections: [
      {
        h: "1. Éditeur",
        p: [
          "Le site https://aelvir.fr est édité par Aëlvir, société par actions simplifiée à capital variable, au capital minimum de cent (100) euros, dont le siège social est situé 7 Rue Auguste Rodin, 28630 Le Coudray, France.",
          "Immatriculée au RCS de Chartres sous le numéro 939 631 289. SIRET : 939 631 289 00013. N° TVA intracommunautaire : FR62939631289. Code APE / NAF : 5821Z, édition de jeux électroniques.",
          "Coordonnées : e-mail contact.aelvir@proton.me. Téléphone : 06 60 98 48 16 (président).",
        ],
      },
      {
        h: "2. Directeur de la publication",
        p: "Alain Brochen, en qualité de directeur de la publication.",
      },
      {
        h: "3. Hébergement",
        p: "Le site est hébergé par Infomaniak Network SA, dont le siège social est situé Les Acacias, 1227 Genève, Suisse, immatriculée au Registre du commerce du canton de Genève sous le numéro CH-660.0.059.996-1. Numéro IDE / TVA : EU442008675. Téléphone : +41 22 820 35 44.",
      },
      {
        h: "4. Sous-traitants de stockage des données",
        p: [
          "Le stockage des fichiers du site (pages, images, scripts) et l'acheminement des e-mails issus du formulaire de contact sont assurés par l'hébergeur Infomaniak Network SA, dont les coordonnées figurent au § 3. Aucun CDN ni service cloud distinct n'est utilisé. Le site n'emploie pas de base de données.",
          "Les messages reçus à l'adresse de contact sont conservés dans une boîte e-mail Proton Mail, éditée par Proton AG, Route de la Galaise 32, 1228 Plan-les-Ouates, Suisse. Contact : https://proton.me",
        ],
      },
      {
        h: "5. Propriété intellectuelle",
        p: [
          "Sauf mention contraire, l'ensemble des éléments accessibles sur le site, notamment les textes, graphismes, logos, icônes, images, vidéos, logiciels, bases de données, documents, éléments sonores ainsi que leur mise en forme, sont protégés par les droits de propriété intellectuelle et sont la propriété exclusive d'Aëlvir ou font l'objet d'un droit d'usage régulier.",
          "Toute reproduction, représentation, adaptation, traduction, extraction, réutilisation, exploitation ou diffusion, totale ou partielle, de tout ou partie du site ou de ses contenus, sur quelque support que ce soit, sans autorisation écrite préalable d'Aëlvir, est interdite, sauf disposition légale impérative contraire.",
        ],
      },
      {
        h: "6. Fouille de textes et de données (TDM)",
        p: [
          "Aëlvir s'oppose, dans les conditions prévues par le Code de la propriété intellectuelle, à toute opération de fouille de textes et de données portant sur les contenus du présent site, sauf autorisation écrite préalable.",
          "Cette opposition peut notamment être exprimée par les présentes mentions légales, par les conditions d'utilisation du site, par des réserves de droits ou par tout procédé lisible par machine.",
        ],
      },
      {
        h: "7. Cookies et autres traceurs",
        p: "L'utilisation de cookies et autres traceurs sur le site fait l'objet d'une information spécifique accessible dans la Politique de confidentialité.",
      },
      {
        h: "8. Données personnelles",
        p: [
          "Aëlvir traite les données personnelles collectées via le site dans le respect de la réglementation applicable en matière de protection des données à caractère personnel. L'utilisateur peut exercer ses droits à l'adresse suivante : contact.aelvir@proton.me.",
          "Pour en savoir plus sur les traitements mis en œuvre, les finalités, les bases juridiques, les destinataires, les durées de conservation et les modalités d'exercice des droits, l'utilisateur est invité à consulter la Politique de confidentialité publiée sur le site.",
        ],
      },
      {
        h: "9. Responsabilité",
        p: [
          "Aëlvir s'efforce d'assurer au mieux l'exactitude et la mise à jour des informations diffusées sur le site. Elle ne saurait toutefois garantir l'exhaustivité, l'absence d'erreur ou la disponibilité permanente du site et de l'ensemble de ses contenus.",
          "L'utilisateur demeure seul responsable de l'usage qu'il fait des informations accessibles sur le site.",
        ],
      },
      {
        h: "10. Droit applicable",
        p: "Les présentes mentions légales sont régies par le droit français.",
      },
      {
        h: "Contact",
        p: "Pour toute question concernant les mentions légales : contact.aelvir@proton.me ou 06 60 98 48 16.",
      },
    ],
  },

  privacy: {
    trigger: "Politique de confidentialité",
    title: "Politique de confidentialité",
    updated: "Dernière mise à jour : 4 septembre 2026",
    sections: [
      {
        h: "1. Responsable du traitement",
        p: [
          "Le responsable du traitement des données personnelles collectées sur le site aelvir.fr est :",
          "AËLVIR, société par actions simplifiée (SAS) à capital variable, éditrice du site aelvir.fr.",
          "Siège social : 7 Rue Auguste Rodin, 28630 Le Coudray, France.",
          "RCS Chartres : 939 631 289.",
          "SIRET : 93963128900013.",
          "TVA intracommunautaire : FR62939631289.",
          "Adresse électronique : contact.aelvir@proton.me.",
          "Téléphone : 06 60 98 48 16.",
        ],
      },
      {
        h: "2. Objet de la politique",
        p: [
          "La présente politique de confidentialité a pour objet d'informer toute personne naviguant sur le site aelvir.fr ou utilisant ses services de la manière dont AËLVIR collecte, utilise, conserve et protège ses données personnelles.",
          "Elle s'applique notamment aux traitements réalisés dans le cadre :",
          "de la navigation sur le site ;",
          "de l'utilisation du formulaire de contact, assorti d'une information spécifique affichée au moment de la saisie ;",
          "des échanges avec AËLVIR relatifs à une demande, un projet ou un support.",
        ],
      },
      {
        h: "3. Catégories de données susceptibles d'être collectées",
        p: [
          "Selon les fonctionnalités effectivement utilisées, AËLVIR peut collecter les catégories de données suivantes :",
          "données d'identification : nom et prénom ;",
          "coordonnées : adresse e-mail professionnelle ;",
          "données relatives à la demande : dénomination de l'entreprise, besoin sélectionné dans le menu déroulant du formulaire, contenu du message ;",
          "données techniques : adresse IP, journaux techniques, type de navigateur, appareil utilisé, informations de sécurité, données nécessaires au bon fonctionnement du site ;",
          "toute autre donnée transmise volontairement par l'utilisateur dans le cadre d'un échange avec AËLVIR.",
          "AËLVIR veille à ne collecter que les données pertinentes, adéquates et limitées à ce qui est nécessaire au regard des finalités poursuivies.",
        ],
      },
      {
        h: "4. Finalités des traitements",
        p: [
          "Les données personnelles peuvent être traitées afin de :",
          "répondre aux demandes envoyées via le formulaire de contact et les orienter, en fonction du besoin sélectionné, vers l'interlocuteur compétent ;",
          "gérer les échanges précontractuels avec les prospects ;",
          "assurer l'hébergement, le fonctionnement, la maintenance et la sécurité du site ;",
          "traiter les demandes d'exercice de droits relatives aux données personnelles ;",
          "respecter les obligations légales, réglementaires et probatoires applicables ;",
          "établir la preuve d'un droit et gérer, le cas échéant, les précontentieux et contentieux.",
        ],
      },
      {
        h: "5. Bases juridiques des traitements",
        p: [
          "Selon les cas, les traitements mis en œuvre reposent sur l'une ou plusieurs des bases juridiques suivantes :",
          "le consentement de la personne concernée, exprimé par l'envoi volontaire du formulaire de contact (article 6.1.a du RGPD) ;",
          "l'intérêt légitime d'AËLVIR, notamment pour assurer la sécurité du site, organiser son activité, défendre ses droits et gérer sa relation avec les prospects.",
        ],
      },
      {
        h: "6. Caractère obligatoire ou facultatif des données",
        p: [
          "Dans le formulaire de contact, tous les champs sont obligatoires : nom et prénom, adresse e-mail professionnelle, entreprise, choix du besoin et message.",
          "Ces données sont nécessaires au traitement de la demande : à défaut de leur fourniture, AËLVIR n'est pas en mesure de la traiter.",
        ],
      },
      {
        h: "7. Destinataires des données",
        p: [
          "Les données personnelles sont accessibles, dans la limite de leurs attributions respectives, aux personnes habilitées au sein d'AËLVIR.",
          "Elles peuvent également être communiquées aux prestataires intervenant pour le compte d'AËLVIR, notamment :",
          "Infomaniak, pour l'hébergement du site et l'envoi des messages transmis par le formulaire de contact ;",
          "Proton, pour la conservation des messages reçus dans la boîte e-mail de contact ;",
          "plus généralement, à tout prestataire technique strictement nécessaire au fonctionnement du site, à la sécurité des systèmes ou au respect des obligations légales.",
          "Les données peuvent également être transmises à toute autorité, administration, juridiction, auxiliaire de justice ou conseil lorsque la loi l'exige ou lorsque cette communication est nécessaire à la défense des droits et intérêts d'AËLVIR.",
        ],
      },
      {
        h: "8. Transferts de données",
        p: "Lorsque des données sont traitées ou rendues accessibles depuis un pays situé en dehors de l'Union européenne ou de l'Espace économique européen, AËLVIR veille à ce que ce traitement soit encadré conformément à la réglementation applicable, au moyen d'un mécanisme juridique approprié lorsque celui-ci est requis.",
      },
      {
        h: "9. Durées de conservation",
        p: [
          "Les données personnelles sont conservées pendant une durée n'excédant pas celle nécessaire aux finalités pour lesquelles elles sont collectées, augmentée, le cas échéant, des durées nécessaires au respect des obligations légales, probatoires ou à la défense des droits d'AËLVIR.",
          "En pratique :",
          "les données de contact et de demande sont conservées le temps nécessaire au traitement de la demande, puis au maximum trois ans à compter du dernier échange, sauf demande de suppression antérieure ;",
          "les journaux et données techniques sont conservés pendant la durée utile à la sécurité du site, à la continuité du service et à la constatation d'incidents ;",
          "les données nécessaires à la gestion d'un contentieux sont conservées pendant toute la durée de la procédure et jusqu'à l'épuisement des voies de recours.",
          "Lorsque la conservation n'est plus nécessaire, les données sont supprimées, anonymisées ou archivées de manière sécurisée lorsque cela est légalement permis ou requis.",
        ],
      },
      {
        h: "10. Sécurité et confidentialité",
        p: [
          "AËLVIR met en œuvre des mesures techniques et organisationnelles appropriées afin de préserver la sécurité, l'intégrité et la confidentialité des données personnelles et d'empêcher qu'elles soient déformées, endommagées, divulguées à des tiers non autorisés ou accessibles sans autorisation.",
          "Toutefois, aucun système de transmission ou de stockage n'offrant une sécurité absolue, AËLVIR ne peut garantir une sécurité parfaite en toutes circonstances.",
        ],
      },
      {
        h: "11. Droits des personnes concernées",
        p: "Conformément à la réglementation applicable, toute personne concernée dispose, selon sa situation, des droits suivants :",
        ul: [
          "droit d'accès ;",
          "droit de rectification ;",
          "droit à l'effacement ;",
          "droit à la limitation du traitement ;",
          "droit d'opposition ;",
          "droit à la portabilité des données lorsque ce droit est applicable ;",
          "droit de retirer son consentement à tout moment lorsque le traitement repose sur le consentement, sans affecter la licéité du traitement effectué avant ce retrait ;",
          "droit d'introduire une réclamation auprès de la CNIL.",
        ],
      },
      {
        h: "12. Exercice des droits",
        p: [
          "Les droits peuvent être exercés en adressant une demande à : contact.aelvir@proton.me",
          "La demande doit permettre à AËLVIR d'identifier raisonnablement son auteur avant de la traiter. En cas de doute raisonnable sur l'identité du demandeur, un justificatif complémentaire peut être demandé dans la mesure strictement nécessaire.",
          "Si la personne concernée estime, après avoir contacté AËLVIR, que ses droits ne sont pas respectés, elle peut introduire une réclamation auprès de la CNIL.",
        ],
      },
      {
        h: "13. Cookies et autres traceurs",
        p: [
          "À la date de mise à jour de la présente politique, le site n'utilise pas de cookies ou autres traceurs non essentiels soumis au recueil préalable du consentement.",
          "Le site peut recourir à des cookies ou traceurs strictement nécessaires à son fonctionnement, à sa sécurité, à l'authentification des utilisateurs, à la conservation des choix techniques exprimés par l'utilisateur ou à la fourniture d'un service expressément demandé.",
          "Le site utilise également Matomo, une solution de mesure d'audience auto-hébergée par AËLVIR sur stats.cywiz.fr, configurée de manière à bénéficier de l'exemption de consentement prévue par la CNIL pour les outils de mesure d'audience. Cette mesure d'audience est limitée à la production de statistiques anonymisées de fréquentation et d'utilisation du site, dans le seul but d'en améliorer le fonctionnement et les contenus.",
          "Les données collectées dans ce cadre ne sont pas utilisées à des fins publicitaires, ne sont pas recoupées avec d'autres traitements, et ne permettent pas un suivi de la navigation de l'utilisateur sur d'autres sites.",
          "Conformément aux exigences applicables à ce dispositif, vous pouvez vous opposer à tout moment au suivi de votre navigation par Matomo via le module d'opt-out disponible à l'adresse : https://stats.cywiz.fr/index.php?module=CoreAdminHome&action=optOut&language=fr",
          "Si AËLVIR met en place ultérieurement des cookies ou traceurs soumis au recueil préalable du consentement, une information spécifique sera affichée et le consentement de l'utilisateur sera recueilli lorsqu'il est requis.",
        ],
      },
      {
        h: "14. Mise à jour de la politique",
        p: [
          "AËLVIR peut modifier la présente politique de confidentialité à tout moment, notamment pour tenir compte d'une évolution du site, de ses fonctionnalités, de ses prestataires ou de la réglementation applicable.",
          "La version en vigueur est celle publiée sur le site à la date de consultation.",
        ],
      },
    ],
  },

  cgu: {
    trigger: "CGU",
    title: "Conditions générales d'utilisation",
    sections: [
      {
        h: "À quoi sert ce site",
        p: [
          "aelvir.fr présente le studio Aëlvir et permet de lui écrire. Rien de plus : pas de boutique, pas d'espace client, pas de commande en ligne.",
          "On y trouve une présentation de ce que le studio conçoit (expériences interactives, serious games, simulation, réalité virtuelle) et de Cywiz, l'application de sensibilisation à la cybersécurité qu'il édite. Le détail d'une mission se discute ensuite, hors site, dans un échange et le cas échéant un contrat séparé.",
          "Cette page peut changer (textes, visuels, formulation de l'offre) sans que les CGU aient à être réécrites à chaque fois, tant que le site reste une vitrine et un point de contact.",
          "Consulter le site ou envoyer le formulaire, c'est accepter ces conditions dans leur version affichée ce jour-là. L'identité de l'éditeur, l'hébergeur et les autres mentions obligatoires sont dans Mentions légales, en pied de page, pas ici.",
        ],
      },
      {
        h: "Ce que vous pouvez faire, et ce qui ne l'est pas",
        p: [
          "Vous pouvez lire les pages et nous écrire via le formulaire (un projet, une question, une demande de devis). L'accès est gratuit ; votre connexion et votre matériel sont à votre charge.",
          "Il n'y a pas de compte à créer, donc pas d'identifiants, pas de profil à supprimer, pas de commentaires ni de forum. Personne ne publie de contenu sur ce site, hormis Aëlvir.",
          "Vous n'avez pas le droit d'utiliser le site pour tromper, harceler, extraire les contenus en masse (y compris par un robot ou une fouille de textes et de données), d'y introduire un code malveillant, ou de remplir le formulaire avec de fausses informations. Si vous le faites, c'est vous qui en répondez. Aëlvir peut ignorer la demande et, si besoin, saisir la justice.",
          "Une coupure, une maintenance ou un incident technique peut rendre le site indisponible sans préavis. Ce n'est pas une défaillance dont Aëlvir garantit l'absence.",
        ],
      },
      {
        h: "Vos données",
        p: "Le seul endroit où le site recueille des données personnelles, c'est le formulaire : nom, organisation, e-mail, type de demande, message. Pour y accéder, les corriger, les effacer ou vous y opposer : contact.aelvir@proton.me. Le détail (finalités, durées, destinataires, cookies et mesure d'audience) est dans la Politique de confidentialité.",
      },
      {
        h: "Ce qui appartient à Aëlvir",
        p: "Textes, images, logo, graphismes, code : tout cela est protégé. Aëlvir en est propriétaire, ou l'utilise avec un droit régulier. Les lire sur le site est autorisé. Les copier, les réutiliser, les détourner à des fins commerciales ou publicitaires, sans accord écrit, ne l'est pas.",
      },
      {
        h: "Limites de notre responsabilité",
        p: [
          "Nous soignons ce qui est publié. Cela n'empêche pas une erreur, un oubli ou une information devenue caduque. Rien sur le site n'a valeur de contrat. Vous restez responsable de la lecture que vous en faites.",
          "Aëlvir n'est pas responsable d'un événement extérieur au site (panne réseau, force majeure, acte d'un tiers). La protection de votre matériel reste de votre ressort.",
          "Certains liens peuvent mener vers des sites ou services édités par des tiers. Dans ce cas, Aëlvir ne maîtrise pas leurs contenus, leurs mises à jour ni leurs pratiques. Si un site inconnu pointe vers aelvir.fr, Aëlvir n'en répond pas non plus.",
          "Nous protégeons les données du formulaire autant que raisonnablement possible. Personne ne peut promettre une sécurité absolue.",
        ],
      },
      {
        h: "En cas de désaccord",
        p: "Ces conditions relèvent du droit français. Avant tout tribunal, on cherche d'abord un accord. Si ça ne suffit pas, les juridictions françaises compétentes tranchent. Elles s'appliquent tant que vous utilisez le site.",
      },
    ],
  },

  footerExtra: {
    tagline:
      "Studio d'expériences interactives sur mesure : serious games 2D/3D et réalité virtuelle. Éditeur de {cywiz}, application de sensibilisation à la cybersécurité.",
  },
};
